import {
  TextChatMessage,
  TextChatMessageDateGroup,
  TextChatMessageListItem,
} from "../TextChat.types";

export type TextChatScrollSnapshot = {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
};

export type TextChatScrollBehavior = "auto" | "smooth";

export type TextChatScrollDecision = {
  shouldScrollToBottom: boolean;
  shouldShowNewMessageAffordance: boolean;
  behavior: TextChatScrollBehavior;
};

export type TextChatMessageRangeChange =
  | "initial"
  | "prepend"
  | "append"
  | "replace"
  | "unchanged";

export const DEFAULT_TEXT_CHAT_BOTTOM_THRESHOLD_PX = 96;

export const getMessageDateKey = (date: Date) => date.toDateString();

export const getMessageDateLabel = (date: Date, locale?: string) =>
  date.toLocaleDateString(locale);

const getSenderGroupKey = (message: TextChatMessage) =>
  message.sender?.id ?? message.senderKind;

const toListItem = (
  message: TextChatMessage,
  index: number,
  messages: TextChatMessage[],
): TextChatMessageListItem => {
  const previousMessage = messages[index - 1];
  const nextMessage = messages[index + 1];
  const senderKey = getSenderGroupKey(message);

  return {
    message,
    previousMessage,
    nextMessage,
    isFirstInSenderGroup:
      !previousMessage || getSenderGroupKey(previousMessage) !== senderKey,
    isLastInSenderGroup:
      !nextMessage || getSenderGroupKey(nextMessage) !== senderKey,
  };
};

export const groupTextChatMessagesByDate = (
  messages: TextChatMessage[],
  locale?: string,
): TextChatMessageDateGroup[] => {
  const groupsByDate = new Map<string, TextChatMessage[]>();

  messages.forEach((message) => {
    const dateKey = getMessageDateKey(message.date);
    const existingMessages = groupsByDate.get(dateKey) ?? [];

    groupsByDate.set(dateKey, [...existingMessages, message]);
  });

  return Array.from(groupsByDate.entries()).map(([dateKey, groupMessages]) => ({
    dateKey,
    label: getMessageDateLabel(groupMessages[0].date, locale),
    messages: groupMessages.map(toListItem),
  }));
};

export const getDistanceFromBottom = ({
  scrollTop,
  scrollHeight,
  clientHeight,
}: TextChatScrollSnapshot) => scrollHeight - scrollTop - clientHeight;

export const isNearTextChatBottom = (
  snapshot: TextChatScrollSnapshot,
  thresholdPx = DEFAULT_TEXT_CHAT_BOTTOM_THRESHOLD_PX,
) => getDistanceFromBottom(snapshot) <= thresholdPx;

export const getScrollTopAfterPrepend = (
  previousSnapshot: Pick<TextChatScrollSnapshot, "scrollTop" | "scrollHeight">,
  nextScrollHeight: number,
) => nextScrollHeight - previousSnapshot.scrollHeight + previousSnapshot.scrollTop;

export const getNewMessageScrollDecision = ({
  wasNearBottom,
  prefersReducedMotion,
}: {
  wasNearBottom: boolean;
  prefersReducedMotion: boolean;
}): TextChatScrollDecision => ({
  shouldScrollToBottom: wasNearBottom,
  shouldShowNewMessageAffordance: !wasNearBottom,
  behavior: prefersReducedMotion ? "auto" : "smooth",
});

export const getMessageRangeChange = ({
  previousCount,
  nextCount,
  previousFirstId,
  nextFirstId,
  previousLastId,
  nextLastId,
}: {
  previousCount: number;
  nextCount: number;
  previousFirstId?: string;
  nextFirstId?: string;
  previousLastId?: string;
  nextLastId?: string;
}): TextChatMessageRangeChange => {
  if (previousCount === nextCount) {
    return "unchanged";
  }

  if (previousCount === 0 && nextCount > 0) {
    return "initial";
  }

  if (nextCount > previousCount) {
    if (previousFirstId !== nextFirstId && previousLastId === nextLastId) {
      return "prepend";
    }

    if (previousFirstId === nextFirstId && previousLastId !== nextLastId) {
      return "append";
    }
  }

  return "replace";
};
